import React, { useEffect, useState } from 'react';
import { ExecutionTemplate, CreateExecutionTemplateRequest, ExecutionPipelinesApiClient, Icons } from 'process-pipelines-common';
import { TemplateEditor, TabType } from './TemplateEditor';

export interface ScopeContext {
  scopeLabel: string;
  executionScope: 'ProcessElement' | 'ProcessThread' | 'Process';
  scopeReferenceID: number;
}

export interface TemplateEditorModalProps {
  // Required
  isOpen: boolean;
  apiClient: ExecutionPipelinesApiClient;
  onClose: () => void;

  // Mode-specific
  mode: 'create' | 'edit';
  template: ExecutionTemplate | null; // Required for edit, null for create
  scope?: ScopeContext; // Required for create, optional for edit

  // Optional handlers
  onSave?: (template: ExecutionTemplate) => void;
  onSuccess?: (template: ExecutionTemplate) => void;
  onError?: (err: Error) => void;
  onExecute?: (executionID: string, templateName: string) => void; // Called when user executes template

  // UI customization
  initialTab?: TabType;
  title?: string;
  readOnly?: boolean;
  saving?: boolean;
  error?: string | null;
  onClearError?: () => void;
  onShowToast?: (type: 'success' | 'error', message: string) => void; // Optional toast callback
}

/**
 * UNIFIED Template Editor Modal Component
 * Single reusable component for both CREATE and EDIT modes
 * Handles theme detection to match the hosting app's color scheme
 * Replaces both TemplateEditorModal and CreateTemplateModal
 *
 * Features:
 * - Full-height side panel (slides from right, slides in from same side always)
 * - Full-screen toggle (button in header or press F key)
 * - Theme-aware styling (adapts to host app's dark/light theme)
 * - Single close button in header (no duplicate Close button in TemplateEditor)
 * - Scope info panel (create mode only)
 * - Error handling with dismiss
 * - Smooth animations
 * - Responsive layout in both side-panel and full-screen modes
 * - Consistent styling across Flow Studio and Flow Pipelines
 * - Preserves data when toggling between modes
 */
export const TemplateEditorModal: React.FC<TemplateEditorModalProps> = ({
  isOpen,
  mode,
  template,
  scope,
  apiClient,
  onSave,
  onSuccess,
  onError,
  onExecute,
  onClose,
  initialTab = 'basics',
  title,
  readOnly = false,
  saving = false,
  error = null,
  onClearError,
  onShowToast,
}) => {
  // Full-screen state
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [executing, setExecuting] = useState(false);

  // Use Flow Studio's light theme colors consistently across all apps
  // (no theme detection - ensures modal looks identical in Flow Studio and Flow Pipelines)
  const appTheme = 'light';

  // Inject Flow Studio's light theme CSS (consistent across all apps)
  useEffect(() => {
    if (!isOpen) return;

    const styleId = 'template-editor-modal-light-theme';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      .template-editor-modal-overlay { animation: slideIn 0.3s ease-out; }
      .template-editor-modal-content {
        background-color: #020617 !important;
        color: #e5e7eb !important;
      }
      .template-editor-modal-content * {
        background-color: #020617 !important;
        color: #e5e7eb !important;
      }
      .template-editor-modal-content input,
      .template-editor-modal-content textarea,
      .template-editor-modal-content select {
        background-color: rgb(15, 23, 42) !important;
        color: #e5e7eb !important;
        border: 1px solid rgba(255, 255, 255, 0.06) !important;
      }
      .template-editor-modal-content input:focus,
      .template-editor-modal-content textarea:focus,
      .template-editor-modal-content select:focus {
        outline: 2px solid #30a046 !important;
        outline-offset: 2px !important;
      }
      .template-editor-modal-content input::placeholder,
      .template-editor-modal-content textarea::placeholder {
        color: #9ca3af !important;
      }
      .template-editor-modal-content label {
        color: #e5e7eb !important;
      }
      .template-editor-modal-content h2,
      .template-editor-modal-content p,
      .template-editor-modal-content span {
        color: #e5e7eb !important;
      }
      .template-editor-modal-content button {
        color: #e5e7eb !important;
        border-color: rgba(255, 255, 255, 0.06) !important;
        background-color: rgba(255, 255, 255, 0.05) !important;
      }
      .template-editor-modal-content button:hover {
        background-color: rgba(255, 255, 255, 0.1) !important;
      }
      .template-editor-modal-content [role="tablist"] {
        background-color: #020617 !important;
        border-color: rgba(255, 255, 255, 0.06) !important;
      }
      .template-editor-modal-content [role="tab"] {
        background-color: #020617 !important;
        color: #9ca3af !important;
        border-color: transparent !important;
      }
      .template-editor-modal-content [role="tab"][aria-selected="true"] {
        color: #30a046 !important;
        border-bottom-color: #30a046 !important;
      }
      .template-editor-modal-content [role="tabpanel"] {
        background-color: #020617 !important;
      }
      /* Button hover effects */
      button:hover:not(:disabled) {
        color: #10b981 !important;
      }
    `;

    document.head.appendChild(style);
    return () => document.getElementById(styleId)?.remove();
  }, [isOpen]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if ((e.key === 'f' || e.key === 'F') && !e.ctrlKey && !e.metaKey) {
        setIsFullScreen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Validation
  if (!isOpen) return null;
  if (mode === 'create' && !scope) {
    console.error('TemplateEditorModal: create mode requires scope prop');
    return null;
  }
  if (mode === 'edit' && !template) {
    return null;
  }

  // Build theme-aware styles
  const themeStyles = getThemeStyles(appTheme, isFullScreen);

  // Determine title
  const modalTitle = title || (mode === 'create' ? 'Create Template' : 'Edit Template');

  const handleSave = async (templateData: Partial<ExecutionTemplate>) => {
    // If the host supplied an `onSave` handler, it owns the create/update API
    // call (and its own success/error toasts, loading state, and close/navigate
    // behavior — see CreateTemplateModal and TemplatesPage). In that case this
    // modal must ONLY hand off the gathered form data and must NOT also call
    // the API itself, otherwise the template gets created/updated twice for a
    // single Save click. Only fall back to calling the API directly when no
    // `onSave` was provided (e.g. TopToolbar's usage of this modal).
    if (onSave) {
      onSave(templateData as ExecutionTemplate);
      return;
    }

    try {
      if (mode === 'create') {
        // Only send fields the create endpoint accepts. templateData carries the
        // full ExecutionTemplate shape (including server-owned fields like resID,
        // tenantID, and the audit columns seeded as placeholders by the "new
        // template" default object), so it must never be spread wholesale into
        // the request body: an empty-string resID fails Guid deserialization on
        // the backend with an unhandled 500 rather than a validation error.
        const createRequest: CreateExecutionTemplateRequest = {
          executionScope: scope!.executionScope,
          scopeReferenceID: scope!.scopeReferenceID,
          name: templateData.name ?? '',
          description: templateData.description,
          longDescription: templateData.longDescription,
          displayName: templateData.displayName,
          slug: templateData.slug ?? '',
          iconUrl: templateData.iconUrl,
          imageUrl: templateData.imageUrl,
          configuration: templateData.configuration || '{}',
          concurrencyBehavior: templateData.concurrencyBehavior,
          defaultTriggerMode: templateData.defaultTriggerMode,
          executionTemplateTypeID: templateData.executionTemplateTypeID,
          executionTemplateCategoryID: templateData.executionTemplateCategoryID,
        };

        // CRITICAL FIX: Actually call the API to create the template!
        const createdTemplate = await apiClient.createExecutionTemplate(createRequest);
        onShowToast?.('success', `Template "${createdTemplate.name}" created successfully!`);
        onSuccess?.(createdTemplate);
      } else {
        const updated = { ...template, ...templateData } as ExecutionTemplate;
        // CRITICAL FIX: Actually call the API to update the template!
        const updatedTemplate = await apiClient.updateExecutionTemplate(updated.executionTemplateID, templateData);
        onShowToast?.('success', `Template "${updatedTemplate.name}" updated successfully!`);
        onSuccess?.(updatedTemplate);
      }
      onClose();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Save failed');
      const message = error.message || 'Failed to save template';
      onShowToast?.('error', `Save failed: ${message}`);
      onError?.(error);
    }
  };

  // Handle template execution
  const handleExecuteTemplate = async () => {
    if (!template?.executionTemplateID) return;

    setExecuting(true);
    try {
      const result = await apiClient.executeExecutionTemplate(template.executionTemplateID);
      onShowToast?.('success', 'Execution started!');
      onExecute?.(result.executionID, template.name);
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start execution';
      onShowToast?.('error', `Execution failed: ${message}`);
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div style={themeStyles.overlay} onClick={onClose}>
      <div
        id="template-editor-modal-theme-aware"
        style={themeStyles.modal}
        onClick={(e) => e.stopPropagation()}
        className="template-editor-modal-overlay"
      >
        {/* Header: Single title + close button (no duplicate Close button in TemplateEditor) */}
        <div style={themeStyles.header}>
          <div>
            <h2 style={themeStyles.title}>{modalTitle}</h2>
            {/* Scope info - create mode only */}
            {mode === 'create' && scope && (
              <>
                <div style={themeStyles.scopeInfo}>
                  <span style={themeStyles.scopeLabel}>
                    <Icons.CheckCircle /> Scoped to: {scope.scopeLabel}
                  </span>
                  <span style={themeStyles.scopeBadge}>
                    {scope.executionScope === 'ProcessElement'
                      ? 'Node'
                      : scope.executionScope === 'ProcessThread'
                      ? 'Thread'
                      : 'Process'}
                  </span>
                </div>
                <p style={themeStyles.scopeHint}>
                  You cannot change the scope here. If needed, rescope via the Jobs screen.
                </p>
              </>
            )}
          </div>
          {/* Action buttons - Full Screen and Close */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* Full Screen Toggle Button */}
            <button
              onClick={() => setIsFullScreen(!isFullScreen)}
              disabled={saving}
              style={themeStyles.fullScreenButton}
              aria-label={isFullScreen ? 'Exit full screen' : 'Enter full screen'}
              title={isFullScreen ? 'Exit full screen (F)' : 'Full screen (F)'}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 32 32"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                style={{
                  transform: isFullScreen ? 'scaleX(-1) scaleY(-1)' : 'none',
                  transition: 'transform 0.3s ease',
                }}
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 21v3.5a.5.5 0 00.5.5H7M3 11V7.5a.5.5 0 01.5-.5H7m18 0h3.5a.5.5 0 01.5.5V11m0 10v3.5a.5.5 0 01-.5.5H25"
                />
              </svg>
            </button>
            {/* Close button */}
            <button
              onClick={onClose}
              disabled={saving}
              style={themeStyles.closeButton}
              aria-label="Close modal"
              title="Close (Esc)"
            >
              ×
            </button>
          </div>
        </div>

        {/* Error banner - dismissible */}
        {error && (
          <div style={themeStyles.errorBanner}>
            <span style={themeStyles.errorIcon}>⚠️</span>
            <span>{error}</span>
            <button
              onClick={onClearError}
              style={themeStyles.closeErrorButton}
              aria-label="Dismiss error"
            >
              ✕
            </button>
          </div>
        )}

        {/* Execute Button - Above Tab Section (edit mode only) */}
        {mode === 'edit' && template?.executionTemplateID && (
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
          }}>
            <button
              onClick={handleExecuteTemplate}
              disabled={saving || executing || !template.executionTemplateID}
              style={{
                padding: '8px 16px',
                backgroundColor: executing ? '#666' : '#30a046',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: executing ? 'wait' : 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'background-color 0.2s ease',
                opacity: (saving || executing || !template.executionTemplateID) ? 0.6 : 1,
              }}
              aria-label="Execute template"
              title={executing ? 'Executing...' : 'Execute template'}
            >
              <span>{executing ? '⟳' : '▶'}</span>
              <span>{executing ? 'Executing...' : 'Execute'}</span>
            </button>
          </div>
        )}

        {/* Editor container - no Close button here */}
        <div style={themeStyles.editorContainer} className="template-editor-modal-content">
          {mode === 'create' && scope ? (
            <TemplateEditor
              template={{
                executionTemplateID: 0,
                name: '',
                description: '',
                longDescription: '',
                displayName: '',
                slug: '',
                iconUrl: '',
                imageUrl: '',
                enabled: true,
                executionScope: scope.executionScope,
                scopeReferenceID: scope.scopeReferenceID,
                concurrencyBehavior: 'Allow',
                defaultTriggerMode: 'Execute',
                tenantID: 0,
                deleted: false,
                archived: false,
                createdOn: new Date().toISOString(),
                lastModifiedOn: new Date().toISOString(),
                executionCount: 0,
                isActive: true,
                resID: '',
              } as ExecutionTemplate}
              apiClient={apiClient}
              onSave={handleSave}
              onClose={onClose}
              initialTab={initialTab}
              readOnly={false}
              readOnlyScope={true}
              isCreating={true}
              hideCloseButton={true}
            />
          ) : template ? (
            <TemplateEditor
              template={template}
              apiClient={apiClient}
              onSave={handleSave}
              onClose={onClose}
              initialTab={initialTab}
              readOnly={readOnly}
              hideCloseButton={true}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

const getThemeStyles = (theme: 'light' | 'dark', isFullScreen: boolean = false) => {
  // Always use Flow Studio's light theme colors for consistency across all apps
  return {
    overlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: isFullScreen ? 'center' : 'flex-end',
      zIndex: 1000,
      transition: 'justify-content 0.3s ease',
    },
    modal: {
      display: 'flex',
      flexDirection: 'column' as const,
      width: isFullScreen ? '95%' : '100%',
      maxWidth: isFullScreen ? '1400px' : '600px',
      height: isFullScreen ? '95vh' : '100vh',
      backgroundColor: '#020617',
      borderRadius: isFullScreen ? '8px' : '0',
      boxShadow: isFullScreen ? '0 10px 40px rgba(0, 0, 0, 0.5)' : '-2px 0 8px rgba(0, 0, 0, 0.5)',
      overflow: 'hidden',
      transition: 'width 0.3s ease, maxWidth 0.3s ease, height 0.3s ease, borderRadius 0.3s ease',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      padding: '20px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
      backgroundColor: 'rgba(15, 23, 42, 0.8)',
      flexShrink: 0,
    },
    title: {
      margin: '0 0 8px 0',
      fontSize: '18px',
      fontWeight: '600',
      color: '#e5e7eb',
    },
    scopeInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginTop: '8px',
    },
    scopeLabel: {
      fontSize: '13px',
      fontWeight: '500',
      color: '#30a046',
    },
    scopeBadge: {
      display: 'inline-block',
      padding: '2px 8px',
      backgroundColor: 'rgba(96, 165, 250, 0.1)',
      color: '#30a046',
      borderRadius: '0',
      fontSize: '11px',
      fontWeight: '600',
    },
    scopeHint: {
      margin: '8px 0 0 0',
      fontSize: '12px',
      color: '#9ca3af',
    },
    fullScreenButton: {
      background: 'none',
      border: 'none',
      fontSize: '20px',
      cursor: 'pointer',
      color: '#9ca3af',
      padding: '0',
      width: '32px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'color 0.2s',
      flexShrink: 0,
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#9ca3af',
      padding: '0',
      width: '32px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'color 0.2s',
      flexShrink: 0,
    },
    errorBanner: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px 20px',
      backgroundColor: 'rgba(239, 68, 68, 0.15)',
      color: '#fca5a5',
      fontSize: '13px',
      borderBottom: '1px solid rgba(239, 68, 68, 0.3)',
      flexShrink: 0,
    },
    errorIcon: {
      fontSize: '16px',
    },
    closeErrorButton: {
      background: 'none',
      border: 'none',
      color: '#fca5a5',
      cursor: 'pointer',
      padding: '0',
      marginLeft: 'auto',
      fontSize: '16px',
    },
    editorContainer: {
      flex: 1,
      overflow: 'auto',
      overflowX: 'hidden' as const,
      overflowY: 'auto' as const,
      backgroundColor: '#020617',
      position: 'relative' as const,
      padding: isFullScreen ? '16px 20px' : '20px',
      minHeight: 0,
      boxSizing: 'border-box' as const,
    },
  };
};
